import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@/components/breadcrumb';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_details/_installations',
)({
    component: RouteComponent,
});

function RouteComponent() {
    const auth = useAuthentication();
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();
    const location = useLocation();

    const objectQuery = useQuery({
        ...portaalQueryOptions.portal.objects.view({
            ObjectId: params.locationId,
        }),
        enabled: !!auth.isAuthenticated,
    });

    const projectQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({
            ElectricalProjectId: params.projectId,
        }),
        enabled: !auth.isAuthenticated,
    });

    // Extract current section from URL
    const pathSegments = location.pathname.split('/');
    const currentSection = pathSegments[pathSegments.length - 2]; // Get second to last segment
    const itemId = pathSegments[pathSegments.length - 1]; // Get the last segment (the ID)

    // Conditional queries based on section
    const distributionBoardQuery = (currentSection === 'final-curcuit' || currentSection === 'distribution-boards') && itemId
        ? portaalQueryOptions.electrical.distributionBoards.view({ ElectricalProjectDistributionBoardId: itemId })
        : null;

    const finalCircuitQuery = (currentSection === 'inverters' || currentSection === 'machines' || currentSection === 'other') && itemId
        ? portaalQueryOptions.electrical.finalcircuits.view({ ElectricalProjectFinalCircuitId: itemId })
        : null;

    // Conditional queries based on section
    const { data: distributionBoardData } = useQuery({
        ...portaalQueryOptions.electrical.distributionBoards.view({ ElectricalProjectDistributionBoardId: itemId || '' }),
        enabled: !!distributionBoardQuery,
    });

    const { data: finalCircuitData } = useQuery({
        ...portaalQueryOptions.electrical.finalcircuits.view({ ElectricalProjectFinalCircuitId: itemId || '' }),
        enabled: !!finalCircuitQuery,
    });

    // Get item name based on available data
    const getItemName = () => {
        if (distributionBoardData?.DistributionBoard?.Identifier) {
            return distributionBoardData.DistributionBoard.Identifier;
        }
        if (finalCircuitData?.FinalCircuit?.Identifier) {
            return finalCircuitData.FinalCircuit.Identifier;
        }
        return '';
    };

    return (
        <>
            <div className="max-w-screen-2xl flex flex-col flex-1 p-8 pb-0 overflow-y-auto h-full">
                <Breadcrumb
                    items={[
                        {
                            name: auth.isAuthenticated
                                ? objectQuery.data?.Object?.Name || t('module-electrical.projects.title')
                                : projectQuery.data?.Project?.ProjectObject?.Name || t('module-electrical.projects.title'),
                            to: '/customer/locations/$locationId',
                            params: { locationId: params.locationId },
                        },
                        {
                            isActive: true,
                            name: t('module-electrical.name'),
                            subcategory: {
                                name: t(`module-electrical.${currentSection}.title`),
                                to: `/customer/locations/$locationId/electrical/projects/$projectId/${currentSection}`,
                                params: {
                                    locationId: params.locationId,
                                    projectId: params.projectId,
                                },
                            },
                        },
                        ...(getItemName()
                            ? [{
                                    isActive: true,
                                    name: getItemName(),
                                    to: location.pathname,
                                    params: { locationId: params.locationId, projectId: params.projectId },
                                }]
                            : []),
                    ]}
                />
                <Outlet />
            </div>
        </>
    );
}
